import { cx } from 'class-variance-authority'
import type { InputHTMLAttributes } from 'react'
import React, { forwardRef, useContext } from 'react'

import { InputField, InputFieldContext } from '../InputField/InputField'

import styles from './TextInput.module.scss'

// *************************************************************
// TODO: Use icons from the package
// *************************************************************

const ClearIcon = () => (
  <svg
    fill="none"
    height="16"
    viewBox="0 0 16 16"
    width="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      height="17"
      id="mask0_2152_4138"
      maskUnits="userSpaceOnUse"
      style={{ maskType: 'alpha' }}
      width="17"
      x="-1"
      y="-1"
    >
      <rect
        fill="currentColor"
        height="16"
        width="16"
        x="-0.090332"
        y="-0.200195"
      />
    </mask>
    <g mask="url(#mask0_2152_4138)">
      <path
        d="M7.9095 8.50214L9.95817 10.551C10.0505 10.6432 10.1666 10.6904 10.3063 10.6926C10.446 10.6948 10.5642 10.6475 10.6608 10.551C10.7574 10.4543 10.8057 10.3372 10.8057 10.1996C10.8057 10.0621 10.7574 9.94497 10.6608 9.84831L8.61201 7.79964L10.6608 5.75098C10.7531 5.65864 10.8003 5.54259 10.8025 5.40281C10.8046 5.26314 10.7574 5.14497 10.6608 5.04831C10.5642 4.95175 10.4471 4.90347 10.3095 4.90347C10.1719 4.90347 10.0548 4.95175 9.95817 5.04831L7.9095 7.09714L5.86084 5.04831C5.7685 4.95609 5.65245 4.90886 5.51267 4.90664C5.37301 4.90453 5.25484 4.95175 5.15817 5.04831C5.06162 5.14497 5.01334 5.26209 5.01334 5.39964C5.01334 5.5372 5.06162 5.65431 5.15817 5.75098L7.20701 7.79964L5.15817 9.84831C5.06595 9.94064 5.01873 10.0567 5.01651 10.1965C5.01439 10.3361 5.06162 10.4543 5.15817 10.551C5.25484 10.6475 5.37195 10.6958 5.50951 10.6958C5.64706 10.6958 5.76417 10.6475 5.86084 10.551L7.9095 8.50214ZM7.91067 14.133C7.03467 14.133 6.21128 13.9668 5.4405 13.6343C4.66973 13.3019 3.99928 12.8507 3.42917 12.2808C2.85906 11.7109 2.40767 11.0408 2.07501 10.2703C1.74245 9.49986 1.57617 8.6767 1.57617 7.80081C1.57617 6.92481 1.74239 6.10142 2.07484 5.33064C2.40728 4.55986 2.85845 3.88942 3.42834 3.31931C3.99823 2.7492 4.66839 2.29781 5.43884 1.96514C6.20928 1.63259 7.03245 1.46631 7.90834 1.46631C8.78434 1.46631 9.60773 1.63253 10.3785 1.96497C11.1493 2.29742 11.8197 2.74859 12.3898 3.31848C12.9599 3.88836 13.4113 4.55853 13.744 5.32898C14.0766 6.09942 14.2428 6.92259 14.2428 7.79847C14.2428 8.67447 14.0766 9.49786 13.7442 10.2686C13.4117 11.0394 12.9606 11.7099 12.3907 12.28C11.8208 12.8501 11.1506 13.3015 10.3802 13.6341C9.60973 13.9667 8.78656 14.133 7.91067 14.133Z"
        fill="var(--gray-a11)"
        fillOpacity="1"
      />
    </g>
  </svg>
)

interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  clearable?: boolean
  onClear?: () => void
}

const Input = forwardRef<HTMLInputElement, TextInputProps>(
  ({ className, disabled, clearable, onClear, onChange, ...props }, ref) => {
    const context = useContext(InputFieldContext)

    if (!context)
      throw new Error('TextInput.Input must be used within InputField.Root')

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation()
      const syntheticEvent = {
        target: { value: '' }
      } as React.ChangeEvent<HTMLInputElement>

      onChange?.(syntheticEvent)
      onClear?.()

      const inputElement = e.currentTarget.parentElement?.querySelector('input')

      inputElement?.focus()
    }

    return (
      <div className={styles.TextInputWrapper}>
        <input
          aria-describedby={
            context.hasValidation ? context.validationId : undefined
          }
          aria-disabled={disabled || context.disabled}
          aria-invalid={context.error}
          aria-readonly={context.readOnly}
          className={cx(styles.TextInput, className)}
          disabled={disabled || context.disabled}
          id={context.id}
          onChange={onChange}
          readOnly={context.readOnly}
          ref={ref}
          {...props}
        />
        {clearable && props.value && (
          <button
            aria-label="Clear input"
            className={styles.TextInputClearButton}
            onClick={handleClear}
            type="button"
          >
            <ClearIcon />
          </button>
        )}
      </div>
    )
  }
)

Input.displayName = 'TextInput.Input'

export const TextInput = {
  Root: InputField.Root,
  Container: InputField.Container,
  Label: InputField.Label,
  Input,
  Validation: InputField.Validation,
  HelperText: InputField.HelperText,
  AffixContainer: InputField.AffixContainer,
  Prefix: InputField.Prefix,
  Suffix: InputField.Suffix
}
